import { useCallback } from 'react';
import { useMiNavigation } from 'hooks/useNavigation';

interface UseGridActionsProps {
    popupRedirectAfter: boolean;
    setGridDataChanged: (changed: boolean) => void;
    reloadGridData?: () => Promise<void> | void;
    gridName: string;
    setShowEditSelector: (selector: string) => void;
    massActionsProps: any;
    setGridView: (view: string) => void;
    setUserInteracted: (interacted: boolean) => void;
    saveColumns?: (
        columnsWidth: any,
        visibleColumns: any,
        fullwidthMode: string,
        gridView: string,
        version: number,
    ) => void;
    columnsWidth: any;
    visibleColumns: any;
    fullwidthMode: string;
    smartConfigVersion: number;
}

interface UseGridActionsReturn {
    afterDataSave: (response: any) => void;
    onChangeView: (value: string) => void;
}

export const useGridActions = ({
    popupRedirectAfter,
    setGridDataChanged,
    reloadGridData,
    gridName,
    setShowEditSelector,
    massActionsProps,
    setGridView,
    setUserInteracted,
    saveColumns,
    columnsWidth,
    visibleColumns,
    fullwidthMode,
    smartConfigVersion,
}: UseGridActionsProps): UseGridActionsReturn => {
    const navigate = useMiNavigation();

    const afterDataSave = (response: any) => {
        if (response.data && response.data.redirectUrl > '' && response.data.forceRedirect === true) {
            window.location.href = response.data.redirectUrl;
        }

        if (response.data.status === 'WARNING' && response.data.message) {
            alert(response.data.message);
        }

        if (popupRedirectAfter) {
            const redirectUrl =
                response.data && response.data.redirectUrl ? response.data.redirectUrl : (response.redirectUrl ?? '');

            if (redirectUrl > '') {
                if (response.data.download || response.download) {
                    window.location.href = response.data.redirectUrl;
                    return;
                }

                setTimeout(() => navigate(redirectUrl), 300);
            }
        } else {
            if (setGridDataChanged) {
                setGridDataChanged(true);
            }
            if (reloadGridData) {
                if (response.data.showEditPopup === true) {
                    const result = reloadGridData();
                    if (result instanceof Promise) {
                        result.then(() => {
                            const itemSelector = `[data-test='grid_${gridName}_edit_column_${response.data.id}_cell'] button`;
                            setShowEditSelector(itemSelector);
                        });
                    } else {
                        // If reloadGridData returns void, execute the callback immediately
                        const itemSelector = `[data-test='grid_${gridName}_edit_column_${response.data.id}_cell'] button`;
                        setShowEditSelector(itemSelector);
                    }
                } else {
                    reloadGridData();
                }
            }
            if (massActionsProps.setCheckedKeys) {
                massActionsProps.setCheckedKeys([]);
            }
        }
    };

    const onChangeView = useCallback(
        (value: string) => {
            setGridView(value > '' ? value : 'default');
            setUserInteracted(true);
            if (saveColumns) {
                saveColumns(columnsWidth, visibleColumns, fullwidthMode, value, smartConfigVersion);
            }
        },
        [saveColumns, columnsWidth, visibleColumns, fullwidthMode, smartConfigVersion, setGridView, setUserInteracted],
    );

    return {
        afterDataSave,
        onChangeView,
    };
};
